/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { lineString, polygon, point as turfPoint } from '@turf/helpers';
import { length as turfLength } from '@turf/length';
import { along } from '@turf/along';
import { distance } from '@turf/distance';
import circle from '@turf/circle';
import { lineIntersect } from '@turf/line-intersect';
import booleanWithin from '@turf/boolean-within';
import area from '@turf/area';

const keepFirstOfConsecutive = (baseArray) => {
    return baseArray.reduce((acc, curr, index) => {
        if (index === 0 || curr !== baseArray[index - 1] + 1) {
            acc.push(curr);
        }
        return acc;
    }, []);
};

const insertArrayBetween = (baseArray, arrayToAdd, startIndex, endIndex) => {
    const firstPart =
        startIndex === baseArray.length - 1
            ? baseArray
            : baseArray.slice(0, startIndex + 1);
    const secondPart = !endIndex ? [] : baseArray.slice(endIndex + 1);

    return [...firstPart, ...arrayToAdd, ...secondPart];
};

const reorderArrayByIndexes = (baseArray, startIndex, endIndex) => {
    const lastIndex = baseArray.length - 1;
    const checkedEndIndex = endIndex === baseArray.length ? lastIndex : endIndex;
    const checkedStartIndex = startIndex === baseArray.length ? lastIndex : startIndex;

    if (!checkedStartIndex) return baseArray;

    const firstPart = !checkedStartIndex ? baseArray : baseArray.slice(checkedStartIndex);
    const secondPart =
        checkedEndIndex === lastIndex
            ? baseArray
            : baseArray.slice(0, checkedEndIndex + 1);

    return [...firstPart, ...secondPart];
};

const findLargestSegments = (polygonPoints, deletedBaseIndexes) => {
    let maxDistance = 0;

    let startPoint = null;
    let endPoint = null;
    let startIndex = null;
    let endIndex = null;
    let pointsDistance = null;

    let fallbackStartIndex = null;
    let fallbackEndIndex = null;
    let fallbackPointsDistance = null;

    let indexToDelete = null;
    let commonDistance = null;

    for (let i = 0; i < polygonPoints.length - 1; i += 1) {
        const currentPoint = turfPoint(polygonPoints[i]);
        const nextPoint = turfPoint(polygonPoints[i + 1]);
        const nextIndex = i + 1;

        const segDistance = distance(currentPoint, nextPoint);

        if (
            (!deletedBaseIndexes && segDistance > maxDistance) ||
            (deletedBaseIndexes?.length &&
                deletedBaseIndexes.includes(nextIndex) &&
                segDistance > maxDistance)
        ) {
            fallbackStartIndex = startIndex;
            fallbackEndIndex = endIndex;
            fallbackPointsDistance = pointsDistance;

            startPoint = currentPoint;
            endPoint = nextPoint;
            startIndex = i;
            endIndex = nextIndex;
            pointsDistance = segDistance;

            if (!commonDistance) commonDistance = segDistance;
            maxDistance = segDistance;
        } else if (
            pointsDistance !== segDistance &&
            fallbackPointsDistance !== segDistance
        ) {
            commonDistance = segDistance;
        }
    }

    const lastPoint = turfPoint(polygonPoints[polygonPoints.length - 1]);
    const endsDistance = distance(lastPoint, turfPoint(polygonPoints[0]));
    if (endsDistance > maxDistance) {
        maxDistance = endsDistance;
        startPoint = lastPoint;
        endPoint = turfPoint(polygonPoints[0]);
        startIndex = polygonPoints.length - 1;
        endIndex = 0;
        pointsDistance = endsDistance;
        fallbackStartIndex = startIndex;
        fallbackEndIndex = endIndex;
        fallbackPointsDistance = maxDistance;
    }

    if (
        (endIndex || endIndex === 0) &&
        fallbackStartIndex === 0 &&
        endIndex === fallbackStartIndex
    ) {
        indexToDelete = endIndex;
        endPoint = turfPoint(polygonPoints[fallbackEndIndex]);
        endIndex = endIndex > fallbackEndIndex ? fallbackEndIndex : fallbackEndIndex - 1;
        pointsDistance = distance(startPoint, turfPoint(polygonPoints[fallbackEndIndex]));
        fallbackStartIndex = null;
        fallbackEndIndex = null;
        fallbackPointsDistance = null;
    }

    return {
        startPoint,
        endPoint,
        startIndex,
        endIndex,
        fallbackStartIndex,
        fallbackEndIndex,
        commonDistance,
        indexToDelete,
    };
};

const mergeCirclePolygons = (circles) => {
    let allPoints = [];

    let basePolygon = circles[0];
    circles.forEach((circlePolygon, idx) => {
        if (!idx) return;

        let newPoints = [];
        let partialPoints = [];

        const intersectionPoints = lineIntersect(circlePolygon, basePolygon);

        let deletedBaseIndexes = [];
        const baseCoords = basePolygon.geometry.coordinates[0];
        baseCoords.forEach((point, j) => {
            const pointGeo = turfPoint(point);
            const isInsideAnyOtherCircle = booleanWithin(pointGeo, circlePolygon);
            if (!isInsideAnyOtherCircle) {
                const isDuplicate = newPoints.some(
                    (existingPoint) =>
                        existingPoint[0] === point[0] && existingPoint[1] === point[1]
                );

                if (!isDuplicate) newPoints.push(point);
            } else {
                deletedBaseIndexes.push(j);
            }
        });

        deletedBaseIndexes = keepFirstOfConsecutive(deletedBaseIndexes);

        const coords = circlePolygon.geometry.coordinates[0];

        partialPoints = [];
        coords.forEach((point) => {
            const pointGeo = turfPoint(point);

            const isInsideAnyOtherCircle = booleanWithin(pointGeo, basePolygon);
            if (!isInsideAnyOtherCircle) {
                if (
                    partialPoints[0]?.[0] !== point[0] &&
                    partialPoints[0]?.[1] !== point[1]
                ) {
                    partialPoints.push(point);
                }
            }
        });

        let partialFeature =
            partialPoints.length > 2
                ? polygon([[...partialPoints, partialPoints[0]]])
                : null;

        const baseFeature = polygon([[...newPoints, newPoints[0]]]);

        const partialAreaIsEnough = coords.length * 0.2 < partialPoints.length;

        if (partialPoints.length && partialAreaIsEnough) {
            const partialSegment = findLargestSegments(partialPoints);
            if (partialSegment.indexToDelete || partialSegment.indexToDelete === 0) {
                partialPoints =
                    partialSegment.indexToDelete === 0
                        ? partialPoints.slice(1)
                        : insertArrayBetween(
                              partialPoints,
                              [],
                              partialSegment.indexToDelete,
                              partialSegment.indexToDelete + 1
                          );
                partialFeature = polygon([[...partialPoints, partialPoints[0]]]);
            }

            const baseSegment = findLargestSegments(newPoints, deletedBaseIndexes);
            let baseStart = baseSegment.startIndex;
            let baseEnd = baseSegment.endIndex;

            if (intersectionPoints.features.length > 2) {
                const distanceLineStart = lineString([
                    baseSegment.startPoint.geometry.coordinates,
                    partialSegment.endPoint.geometry.coordinates,
                ]);
                const distanceLineEnd = lineString([
                    partialSegment.startPoint.geometry.coordinates,
                    baseSegment.endPoint.geometry.coordinates,
                ]);

                const isBaseSelfIntersected =
                    lineIntersect(distanceLineStart, baseFeature).features.length > 1 ||
                    lineIntersect(distanceLineEnd, baseFeature).features.length > 1;
                const isPartialSelfIntersected =
                    lineIntersect(distanceLineStart, partialFeature).features.length >
                        1 ||
                    lineIntersect(distanceLineEnd, partialFeature).features.length > 1;

                if (isBaseSelfIntersected || isPartialSelfIntersected) {
                    baseStart = baseSegment.secondStartIndex;
                    baseEnd = baseSegment.secondEndIndex;
                }
            }

            const arrangedEndToStart = reorderArrayByIndexes(
                partialPoints,
                partialSegment.endIndex,
                partialSegment.startIndex
            );

            const updatedPoints = insertArrayBetween(
                newPoints,
                arrangedEndToStart,
                baseStart,
                baseEnd
            );

            newPoints = updatedPoints;
            partialPoints = [];
        }

        if (newPoints.length > 0 && partialAreaIsEnough) {
            allPoints = newPoints;
        }

        const newPolygon = polygon([[...allPoints, allPoints[0]]]);
        basePolygon = area(newPolygon) < area(basePolygon) ? basePolygon : newPolygon;
    });

    return basePolygon;
};

export const createLineBuffer = (line, bufferDistance, units = 'miles') => {
    const lineLength = turfLength(line, { units });
    const numberOfPoints = Math.ceil(lineLength / bufferDistance);

    const circles = Array.from({ length: numberOfPoints }, (_, i) => {
        const pointOnLine = along(line, i * bufferDistance * 1.1, { units });
        return circle(pointOnLine.geometry.coordinates, bufferDistance, { units });
    });

    let calculatedPolygon = null;
    try {
        calculatedPolygon = mergeCirclePolygons(circles);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Something went wrong calculating the route buffer.');
    }

    return calculatedPolygon;
};
